/** @internal */
export type NumberFormatParserArgs = ConstructorParameters<
  typeof Intl.NumberFormat
>;

/**
 * Slightly adjusted version of https://github.com/nuragic/Intl.NumberFormatParse
 * Modifications:
 *   - Removed support of SYMBOLS (unit, currency, percentSign), not needed
 *   - Removed unused methods
 *   - Types
 *   - parse: Originally, thousand delimiters followed by only 2 digits were also
 *     converted to valid numbers.
 *     e.g. delimiter "." | input "12.34" | output 1234 (onethousand twohundred thirtyfour)
 *       with the modification, that's NaN
 * @internal
 */
export class NumberFormatParser {
  private formatter: Intl.NumberFormat;

  private getIndex: (d: string) => number | undefined;

  private groupDelimiter: string | undefined;
  private decimalDelimiter: string | undefined;

  private numeralRegExp: RegExp | undefined;
  private groupRegExp: RegExp | undefined;
  private decimalRegExp: RegExp | undefined;

  constructor(locale?: NumberFormatParserArgs[0]) {
    this.formatter = new Intl.NumberFormat(locale);

    const parts = this.formatter.formatToParts(12345.6789);
    const groupPart = parts.find((d) => d.type === 'group');
    const decimalPart = parts.find((d) => d.type === 'decimal');

    const numerals = [
      ...new Intl.NumberFormat(locale, { useGrouping: false }).format(
        9876543210,
      ),
    ].reverse();

    const indexMap = new Map(numerals.map((d, i) => [d, i]));
    this.getIndex = (d) => indexMap.get(d);

    this.numeralRegExp = new RegExp(`[${numerals.join('')}]`, 'g');

    if (groupPart) {
      this.groupRegExp = new RegExp(`[${groupPart.value}]`, 'g');
      this.groupDelimiter = groupPart.value;
    }

    if (decimalPart) {
      this.decimalRegExp = new RegExp(`[${decimalPart.value}]`);
      this.decimalDelimiter = decimalPart.value;
    }
  }

  parse(value: string): number {
    const invalidDelimiter = value.match(
      new RegExp(
        `(\\${this.groupDelimiter}\\d{0,2}($|\\${this.groupDelimiter}))|(\\${this.decimalDelimiter}$)`,
      ),
    );

    if (invalidDelimiter !== null) {
      return NaN;
    }

    let parsed = value.trim();

    if (this.groupRegExp) {
      parsed = parsed.replace(this.groupRegExp, '');
    }

    if (this.decimalRegExp) {
      parsed = parsed.replace(this.decimalRegExp, '.');
    }

    if (this.numeralRegExp) {
      parsed = parsed.replace(
        this.numeralRegExp,
        (d) => this.getIndex(d)?.toString() ?? '',
      );
    }

    return parsed ? +parsed : NaN;
  }
}

let numberParserInstance: NumberFormatParser;

/** @internal */
export function getNumberParser() {
  if (numberParserInstance === undefined) {
    numberParserInstance = new NumberFormatParser('en');
  }

  return numberParserInstance;
}
